import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { supabase } from "../lib/supabase";

const AdminAuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HEARTBEAT_MS = 60_000;

// Stable device fingerprint: cached in localStorage, hashes a few browser
// signals so the same device gets the same id across sessions but two different
// browsers on the same laptop each get their own.
function getDeviceFingerprint() {
  const KEY = "dc-admin-device-fp";
  try {
    const existing = localStorage.getItem(KEY);
    if (existing) return existing;
    const rnd = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    const seed = [
      navigator.userAgent,
      navigator.language,
      `${window.screen.width}x${window.screen.height}`,
      Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      rnd,
    ].join("|");
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
    }
    const fp = `dc-${Math.abs(hash).toString(36)}-${rnd.slice(0, 8)}`;
    localStorage.setItem(KEY, fp);
    return fp;
  } catch {
    return "dc-anon";
  }
}

export function AdminAuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState(null);
  const [dcSessionId, setDcSessionId] = useState(() => {
    try { return sessionStorage.getItem("dc-admin-session-id") || null; } catch { return null; }
  });
  const heartbeatRef = useRef(null);
  const sidRef = useRef(null);
  const sidInflightRef = useRef(null);
  if (sidRef.current === null && dcSessionId) sidRef.current = dcSessionId;

  const verifyAdmin = useCallback(async (accessToken) => {
    if (!accessToken) { setAdmin(null); return null; }
    try {
      const { data } = await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setAdmin(data);
      return data;
    } catch (err) {
      const detail = err?.response?.data?.detail || "Not an admin";
      setAdmin(null);
      setError(detail);
      return null;
    }
  }, []);

  // Mint (once) the server-side session row. Deduped via an in-flight promise
  // so the login call and the auth-state listener can't create two sessions.
  const startSession = useCallback(async (accessToken) => {
    if (!accessToken) return null;
    if (sidRef.current) return sidRef.current;
    if (sidInflightRef.current) return sidInflightRef.current;
    sidInflightRef.current = (async () => {
      try {
        const { data } = await axios.post(
          `${API}/admin/session/start`,
          {
            device_fingerprint: getDeviceFingerprint(),
            user_agent: navigator.userAgent,
          },
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (data?.session_id) {
          sidRef.current = data.session_id;
          setDcSessionId(data.session_id);
          try { sessionStorage.setItem("dc-admin-session-id", data.session_id); } catch { /* noop */ }
          return data.session_id;
        }
        return null;
      } catch (e) {
        // aal1 with MFA enrolled: blocked until the TOTP challenge passes.
        console.warn("session/start failed", e?.response?.data?.detail || e.message);
        return null;
      } finally {
        sidInflightRef.current = null;
      }
    })();
    return sidInflightRef.current;
  }, []);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(async ({ data }) => {
      if (cancelled) return;
      const s = data.session;
      setSession(s);
      if (s) {
        await verifyAdmin(s.access_token);
        await startSession(s.access_token);
      }
      setChecking(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange(async (_evt, s) => {
      setSession(s);
      if (s) {
        await verifyAdmin(s.access_token);
        await startSession(s.access_token);
      } else setAdmin(null);
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [verifyAdmin, startSession]);

  // Heartbeat while the tab is authenticated.
  useEffect(() => {
    if (!session?.access_token || !dcSessionId) return undefined;
    const tick = async () => {
      try {
        await axios.post(
          `${API}/admin/session/heartbeat`,
          {},
          {
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              "x-dc-session-id": dcSessionId,
            },
          }
        );
      } catch (e) {
        if (e?.response?.status === 440) {
          try { sessionStorage.removeItem("dc-admin-session-id"); } catch { /* noop */ }
          sidRef.current = null;
          setDcSessionId(null);
          await supabase.auth.signOut();
          window.location.replace("/admin/login?reason=session_expired");
        } else {
          console.warn("session heartbeat failed:", e?.response?.data?.detail || e.message);
        }
      }
    };
    heartbeatRef.current = setInterval(tick, HEARTBEAT_MS);
    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
  }, [session, dcSessionId]);

  const signIn = useCallback(async (email, password) => {
    setError(null);
    // Password verification happens server-side: the browser never reports
    // success/failure, so lockout counters can't be spoofed or bypassed.
    let tokens;
    try {
      const { data } = await axios.post(`${API}/admin/auth/login`, {
        email,
        password,
        user_agent: navigator.userAgent,
        device_fingerprint: getDeviceFingerprint(),
      });
      tokens = data;
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        (e?.response?.status === 429 ? "Too many failed attempts." : "Sign-in failed");
      setError(typeof msg === "string" ? msg : "Sign-in failed");
      return { ok: false, error: typeof msg === "string" ? msg : "Sign-in failed" };
    }

    const { data: setData, error: setErr } = await supabase.auth.setSession({
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token,
    });
    if (setErr) {
      setError(setErr.message);
      return { ok: false, error: setErr.message };
    }
    const accessToken = setData?.session?.access_token || tokens.access_token;
    const profile = await verifyAdmin(accessToken);
    if (!profile) {
      await supabase.auth.signOut();
      return { ok: false, error: "You don't have admin access." };
    }
    // Mint the server-side session. For MFA accounts this is rejected until
    // the TOTP challenge elevates the JWT to aal2 — the challenge screen
    // retries it right after verifying.
    await startSession(accessToken);
    return { ok: true };
  }, [verifyAdmin, startSession]);

  const signOut = useCallback(async () => {
    try {
      if (session?.access_token && dcSessionId) {
        await axios.post(
          `${API}/admin/session/logout`,
          {},
          {
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              "x-dc-session-id": dcSessionId,
            },
          }
        );
      }
    } catch { /* non-fatal */ }
    try { sessionStorage.removeItem("dc-admin-session-id"); } catch { /* noop */ }
    sidRef.current = null;
    setDcSessionId(null);
    await supabase.auth.signOut();
    setAdmin(null);
    setSession(null);
  }, [session, dcSessionId]);

  const apiFetch = useCallback(
    async (path, opts = {}) => {
      const token = session?.access_token;
      // Every privileged call must carry a live server-side session id.
      const sid = sidRef.current || (await startSession(token));
      return axios({
        url: `${API}${path}`,
        ...opts,
        headers: {
          ...(opts.headers || {}),
          Authorization: token ? `Bearer ${token}` : undefined,
          ...(sid ? { "x-dc-session-id": sid } : {}),
        },
      }).catch((e) => {
        // Session expired mid-flight: bounce to login.
        if (e?.response?.status === 440) {
          try { sessionStorage.removeItem("dc-admin-session-id"); } catch { /* noop */ }
          sidRef.current = null;
          setDcSessionId(null);
          supabase.auth.signOut().finally(() => {
            window.location.replace("/admin/login?reason=session_expired");
          });
        }
        throw e;
      });
    },
    [session, startSession]
  );

  return (
    <AdminAuthContext.Provider
      value={{ session, admin, checking, error, signIn, signOut, apiFetch, dcSessionId, startSession }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth outside provider");
  return ctx;
};
